import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Logo from '@/components/feature/Logo';
import { useGoldPrice } from '@/hooks/useGoldPrice';
import { useLiveAssets } from '@/hooks/useLiveAssets';
import { supabase } from '@/lib/supabase';
import { tradingMarket, hotMarket } from '@/mocks/market';
import { useTradingStatus } from '@/hooks/useTradingStatus';

function Sparkline({ data, color }: { data: number[]; color: string }) {
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const w = 64;
  const h = 24;

  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * w;
    const y = h - ((v - min) / range) * h;
    return [x, y] as const;
  });

  const lineD = pts.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x},${y}`).join(' ');
  const areaD = `${lineD} L${w},${h} L0,${h} Z`;

  const strokeColor = color === 'red' ? '#ef4444' : '#22c55e';
  const fillColor = color === 'red' ? 'rgba(239,68,68,0.08)' : 'rgba(34,197,94,0.08)';

  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="overflow-visible">
      <path d={areaD} fill={fillColor} />
      <path d={lineD} fill="none" stroke={strokeColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// 支持回购的品种（可交易品：黄金、白银、公斤条）
const BUYBACK_NAMES = new Set(['黄金', '白银', '公斤条', '黄金9999']);

// 价格两列宽度（标题列头与行情行共用，保证竖向对齐）
const PRICE_COLS = 'grid grid-cols-2 gap-3 w-[130px] shrink-0';
const SPARK_COL = 'w-14 sm:w-16 shrink-0 flex items-center justify-center';

interface MarketRowProps {
  item: {
    name: string;
    code: string;
    displayPrice: string;
    change: string;
    up: boolean;
    lineColor: string;
    sparkline: number[];
    buyback?: string;
  };
  index: number;
  delayOffset: number;
}

function MarketRow({ item, index, delayOffset }: MarketRowProps) {
  const [pressed, setPressed] = useState(false);

  const handlePointerDown = useCallback(() => setPressed(true), []);
  const handlePointerUp = useCallback(() => setPressed(false), []);
  const handlePointerLeave = useCallback(() => setPressed(false), []);

  return (
    <div
      className={`flex items-center justify-between py-3.5 cursor-pointer transition-all duration-150 ${
        pressed ? 'scale-[0.98] bg-amber-50/60' : ''
      } ${index !== 0 ? 'border-t border-amber-100/40' : ''}`}
      style={{ animationDelay: `${delayOffset + index * 0.05}s` }}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerLeave}
    >
      <div className="flex-1 min-w-0">
        <p className="text-amber-950 text-[15px] font-medium truncate">{item.name}</p>
        <p className="text-amber-400 text-xs mt-0.5">{item.code}</p>
      </div>
      <div className={SPARK_COL}>
        <Sparkline data={item.sparkline} color={item.lineColor} />
      </div>
      <div className={PRICE_COLS}>
        <div className="text-right">
          <p className="text-amber-950 text-[15px] font-bold tabular-nums leading-none">{item.displayPrice}</p>
          <div className="flex items-center justify-end gap-0.5 mt-1">
            <i className={`${item.up ? 'ri-arrow-up-line' : 'ri-arrow-down-line'} text-[10px] ${item.up ? 'text-red-500' : 'text-green-500'}`}></i>
            <span className={`text-[11px] font-medium ${item.up ? 'text-red-500' : 'text-green-500'}`}>{item.change}</span>
          </div>
        </div>
        <div className="text-right">
          {item.buyback ? (
            <p className="text-amber-600 text-[15px] font-semibold tabular-nums leading-none">{item.buyback}</p>
          ) : (
            <p className="text-amber-200 text-[15px] leading-none">—</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default function MarketPage() {
  const navigate = useNavigate();
  const { getPrice, getChange, getDirection, formatPrice, formatBidPrice, getSparkline, isLive } = useGoldPrice();
  const liveAssetsHook = useLiveAssets();
  const { status: tradingStatus } = useTradingStatus();

  // 休市（非交易时段）：行情源本就不推送新报价，不该把「休市」误报成「未连接」
  const marketClosed = !!tradingStatus && tradingStatus.enabled && !tradingStatus.isOpen;

  const [marqueeNotices, setMarqueeNotices] = useState<{ title: string }[]>([]);
  const [marqueeLoading, setMarqueeLoading] = useState(true);
  const [assetChange, setAssetChange] = useState<number | null>(null);
  const prevTotalRef = useRef<number>(0);
  const changeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 跑马灯：动态测量并计算重复份数
  const marqueeRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);
  const [repeatCount, setRepeatCount] = useState(2);
  const [marqueeDuration, setMarqueeDuration] = useState(8);

  useEffect(() => {
    const measure = () => {
      if (measureRef.current && containerRef.current && marqueeNotices.length > 0) {
        const textWidth = measureRef.current.scrollWidth;
        const containerWidth = containerRef.current.clientWidth;
        // 单份内容宽度需要 >= 容器宽度的 1.2 倍，确保文字从右侧进入
        const needed = Math.max(2, Math.ceil(1.2 * containerWidth / textWidth));
        setRepeatCount(needed);

        const singleWidth = textWidth * needed;
        // 滚动速度 70px/s，限制在 4~15 秒
        const duration = Math.max(4, Math.min(15, singleWidth / 70));
        setMarqueeDuration(duration);
      }
    };
    const raf = requestAnimationFrame(measure);
    window.addEventListener('resize', measure);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', measure);
    };
  }, [marqueeNotices]);

  // 拉取跑马灯公告
  useEffect(() => {
    const fetchMarquee = async () => {
      try {
        const { data, error } = await supabase
          .from('notices')
          .select('title')
          .eq('show_on_marquee', true)
          .order('created_at', { ascending: false })
          .limit(5);
        if (!error && data) {
          setMarqueeNotices(data);
        }
      } catch {
        // 静默失败，不阻断页面
      } finally {
        setMarqueeLoading(false);
      }
    };
    fetchMarquee();
  }, []);

  // 合并实时金价到交易行情
  const liveTrading = tradingMarket.map((item) => {
    const price = getPrice(item.name);
    const change = getChange(item.name);
    const dir = getDirection(item.name);
    const isUp = dir === 'up' || (dir === 'flat' && change >= 0);
    const sign = change >= 0 ? '+' : '';
    const digits = item.name.includes('银') || item.name === '白银' ? 3 : 2;
    return {
      ...item,
      displayPrice: price > 0 ? formatPrice(item.name, digits) : item.displayPrice,
      change: `${sign}${change.toFixed(2)}%`,
      up: isUp,
      lineColor: isUp ? 'green' : 'red',
      sparkline: getSparkline(item.name),
      buyback: BUYBACK_NAMES.has(item.name) ? formatBidPrice(item.name, digits) : '',
    };
  });

  // 合并实时金价到热门行情
  const liveHot = hotMarket.map((item) => {
    const price = getPrice(item.name);
    const change = getChange(item.name);
    const dir = getDirection(item.name);
    const isUp = dir === 'up' || (dir === 'flat' && change >= 0);
    const sign = change >= 0 ? '+' : '';
    const digits = item.name.includes('银') || item.name === '白银' ? 3 : 2;
    return {
      ...item,
      displayPrice: price > 0 ? formatPrice(item.name, digits) : item.displayPrice,
      change: `${sign}${change.toFixed(2)}%`,
      up: isUp,
      lineColor: isUp ? 'green' : 'red',
      sparkline: getSparkline(item.name),
      buyback: BUYBACK_NAMES.has(item.name) ? formatBidPrice(item.name, digits) : '',
    };
  });

  // 实时资产：账户可用+已用余额 + 实时浮动盈亏
  const liveAssets = {
    totalAssets: liveAssetsHook.totalAssets,
    profit: liveAssetsHook.floatingProfit,
    profitPercent: liveAssetsHook.profitPercent,
  };

  // 检测总资产变化，显示涨跌提示（首次加载不显示）
  useEffect(() => {
    const current = liveAssets.totalAssets || 0;
    const prev = prevTotalRef.current;
    if (prev !== 0 && Math.abs(current - prev) > 0.01) {
      const diff = current - prev;
      setAssetChange(diff);
      if (changeTimerRef.current) clearTimeout(changeTimerRef.current);
      changeTimerRef.current = setTimeout(() => setAssetChange(null), 3000);
    }
    prevTotalRef.current = current;
    return () => {
      if (changeTimerRef.current) clearTimeout(changeTimerRef.current);
    };
  }, [liveAssets.totalAssets]);

  return (
    <div className="min-h-screen bg-brown-50 pb-28">
      {/* Header */}
      <div className="px-5 pt-safe pb-3 flex items-center justify-between">
        <Logo
          size="md"
        />
        <button
          onClick={() => navigate('/profile')}
          className="w-9 h-9 rounded-full bg-white/80 flex items-center justify-center shadow-sm cursor-pointer active:scale-95 transition-transform"
        >
          <i className="ri-lock-line text-amber-500 text-lg"></i>
        </button>
      </div>

      {/* 行情未连接提示：仅在真正断连（非休市）时显示 */}
      {!isLive && !marketClosed && (
        <div className="mx-3 mt-3 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5 flex items-center gap-2 animate-fade-up">
          <span className="w-2 h-2 rounded-full bg-red-400 flex-shrink-0 animate-pulse" />
          <p className="text-red-600 text-xs">行情未连接，显示价格为默认值</p>
        </div>
      )}
      <div className="mx-3 mt-3 bg-amber-50 rounded-lg px-3 py-2.5 flex items-center gap-2 overflow-hidden border border-amber-100">
        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
          <i className="ri-volume-up-line text-amber-500 text-sm"></i>
        </div>
        <div ref={containerRef} className="overflow-hidden flex-1 relative">
          {/* 隐藏测量元素，用于测单份文字宽度 */}
          <span ref={measureRef} className="absolute invisible whitespace-nowrap text-xs">
            {marqueeNotices.map((n) => n.title).join('    ·    ')}
          </span>
          {marqueeLoading ? (
            <span className="text-amber-700 text-xs">加载公告中...</span>
          ) : marqueeNotices.length > 0 ? (
            <div
              className="animate-marquee"
              style={{ animationDuration: `${marqueeDuration}s` }}
              ref={marqueeRef}
            >
              <span className="text-amber-700 text-xs">
                {Array(repeatCount)
                  .fill(marqueeNotices.map((n) => n.title).join('    ·    '))
                  .join('    ·    ')}
              </span>
              <span className="text-amber-700 text-xs">
                {Array(repeatCount)
                  .fill(marqueeNotices.map((n) => n.title).join('    ·    '))
                  .join('    ·    ')}
              </span>
            </div>
          ) : (
            <span className="text-amber-700 text-xs">暂无最新公告</span>
          )}
        </div>
      </div>

      {/* Total Assets */}
      <div className="px-3 mt-5 animate-fade-up">
        <p className="text-amber-400 text-xs">总资产</p>
        <div className="flex items-end justify-between mt-1 gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-[28px] font-bold text-amber-950 leading-tight tracking-tight">
              ¥ {liveAssets.totalAssets.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
            {assetChange !== null && (
              <span className={`inline-flex items-center text-sm font-bold mt-1 ${assetChange >= 0 ? 'text-red-500' : 'text-green-500'}`}>
                {assetChange >= 0 ? <i className="ri-arrow-up-line mr-0.5"></i> : <i className="ri-arrow-down-line mr-0.5"></i>}
                ¥{Math.abs(assetChange).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            )}
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              <span className="text-amber-400 text-xs flex-shrink-0">持仓盈亏</span>
              <span className={`text-sm font-semibold flex-shrink-0 ${liveAssets.profit >= 0 ? 'text-red-500' : 'text-green-500'}`}>
                ¥{liveAssets.profit.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
              <span className={`text-white text-[10px] px-2 py-0.5 rounded font-medium flex-shrink-0 ${liveAssets.profitPercent >= 0 ? 'bg-red-500' : 'bg-green-500'}`}>
                {liveAssets.profitPercent >= 0 ? '+' : ''}{liveAssets.profitPercent.toFixed(2)}%
              </span>
            </div>
          </div>
          <button
            onClick={() => navigate('/deposit')}
            className="flex-shrink-0 whitespace-nowrap bg-white border border-amber-400 text-amber-600 px-5 py-2.5 rounded-xl flex items-center gap-2 text-sm font-semibold cursor-pointer active:scale-95 transition-all shadow-sm"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-download-line text-base"></i>
            </span>
            入金
          </button>
        </div>
      </div>

      {/* Trading Status Countdown */}
      {tradingStatus && tradingStatus.enabled && (
        <div className="mx-3 mt-4 bg-white rounded-2xl shadow-sm overflow-hidden animate-fade-up">
          <div className="p-4 md:p-5 text-center">
            {tradingStatus.manualPaused ? (
              <>
                <div className="flex items-center justify-center gap-2 mb-2">
                  <span className="w-2 h-2 rounded-full bg-red-500" />
                  <span className="text-red-600 text-sm font-medium">暂停交易中</span>
                </div>
                <p className="text-brown-400 text-xs">
                  {tradingStatus.pauseReason || '行情波动过大，暂停交易'}
                </p>
              </>
            ) : tradingStatus.isOpen ? (
              <>
                <div className="flex items-center justify-center gap-2 mb-2">
                  <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                  <span className="text-green-600 text-sm font-medium">交易中</span>
                </div>
                <p className="text-brown-400 text-xs mb-3">
                  闭盘时间：{tradingStatus.closeTime?.slice(0, 5)}
                </p>
              </>
            ) : (
              <>
                <p className="text-brown-500 text-sm font-medium mb-3">距离开盘时间</p>
                <div className="flex items-center justify-center gap-2 mb-3">
                  {tradingStatus.countdown.split(':').map((part, i) => (
                    <>
                      <div key={i} className="bg-amber-50 rounded-lg px-3 py-2 min-w-[48px]">
                        <span className="text-brown-900 text-xl font-bold tabular-nums">{part}</span>
                      </div>
                      {i < 2 && (
                        <span key={`sep-${i}`} className="text-brown-300 text-lg font-bold">:</span>
                      )}
                    </>
                  ))}
                </div>
                <p className="text-brown-400 text-xs">
                  开盘时间：{tradingStatus.nextOpenTime
                    ? new Date(tradingStatus.nextOpenTime).toLocaleString('zh-CN', {
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false,
                      }).replace(/\//g, '-')
                    : tradingStatus.openTime?.slice(0, 5)}
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {/* Trading Market Card */}
      <div
        className="mx-3 mt-5 bg-white rounded-2xl shadow-sm overflow-hidden animate-fade-up"
        style={{ animationDelay: '0.15s' }}
      >
        <div className="px-3.5 pt-4 pb-2 flex items-center">
          <h3 className="flex-1 text-amber-950 font-bold text-base">交易行情</h3>
          <div className={PRICE_COLS}>
            <span className="text-right text-[11px] text-amber-400">销售</span>
            <span className="text-right text-[11px] text-amber-400">回购</span>
          </div>
        </div>
        <div className="px-3.5 pb-3">
          {liveTrading.map((item, idx) => (
            <MarketRow key={idx} item={item} index={idx} delayOffset={0.2} />
          ))}
        </div>
      </div>

      {/* Hot Market Card */}
      <div
        className="mx-3 mt-4 bg-white rounded-2xl shadow-sm overflow-hidden animate-fade-up"
        style={{ animationDelay: '0.35s' }}
      >
        <div className="px-3.5 pt-4 pb-2 flex items-center">
          <h3 className="flex-1 text-amber-950 font-bold text-base">热门行情</h3>
          <div className={PRICE_COLS}>
            <span className="text-right text-[11px] text-amber-400">销售</span>
            <span className="text-right text-[11px] text-amber-400">回购</span>
          </div>
        </div>
        <div className="px-3.5 pb-3">
          {liveHot.map((item, idx) => (
            <MarketRow key={idx} item={item} index={idx} delayOffset={0.4} />
          ))}
        </div>
      </div>

      {/* Bottom spacing */}
      <div className="h-6" />
    </div>
  );
}